<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear
          absolute
          color="success"
          height="4"
          indeterminate
        ></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.WAREHOUSES.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-warehouses-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="handleCreateClick"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-warehouses-table"
                height="500"
                name="stock-warehouses-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="100"
                :loading="isProcessing"
                dense
                fixed-header
                multi-sort
                filterable
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <span
                    :key="index"
                    v-if="header.value === 'options'"
                    class="d-flex align-center justify-center flex-nowrap"
                  >
                    <v-btn
                      v-if="hasPermissions('stock-warehouses-edit')"
                      icon
                      color="primary"
                      @click="handleEditClick(item.item.id)"
                    >
                      <v-icon dark>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="hasPermissions('stock-warehouses-delete') && !item.item.is_system"
                      icon
                      color="error"
                      @click="handleDeleteClick(item.item)"
                    >
                      <v-icon dark>mdi-delete</v-icon>
                    </v-btn>
                  </span>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ item.item.organization ? item.item.organization.name : "-" }}
                  </template>
                  <template v-else-if="header.value === 'country.name'">
                    {{ item.item.country ? item.item.country.name : "-" }}
                  </template>
                  <template v-else-if="header.value === 'type'">
                    <v-chip x-small :color="item.value === 'physical' ? 'primary' : 'teal'" dark>
                      {{ $t(`STOCK.WAREHOUSES.TYPES.${item.value.toUpperCase()}`) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'is_active'">
                    <v-chip x-small :color="item.value ? 'success' : 'error'" dark>
                      {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'is_system'">
                    <v-chip x-small :color="item.value ? 'warning' : 'grey'" dark>
                      {{ item.value ? $t("STOCK.WAREHOUSES.SYSTEM") : $t("STOCK.WAREHOUSES.CUSTOM") }}
                    </v-chip>
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showDeleteConfirm"
      :content="$t('GENERAL.DELETE_CONFIRM')"
      @confirm="deleteWarehouse"
    ></TPConfirm>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  DELETE_STOCK_WAREHOUSE,
  GET_STOCK_WAREHOUSES,
} from "@/store/stock/stockWarehouses.module";

export default {
  name: "StockListWarehouses",
  components: {
    Table,
    TPConfirm,
  },
  data() {
    return {
      tableName: "stock-warehouses-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      showDeleteConfirm: false,
      selectedWarehouse: null,
      tableItemsMethod: GET_STOCK_WAREHOUSES,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.CODE"),
          value: "code",
          align: "center",
          width: "13%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "22%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"),
          value: "organization.name",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("GENERAL.LABELS.TYPE"),
          value: "type",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.VIRTUAL_SUBTYPE"),
          value: "virtual_subtype",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("GENERAL.LABELS.COUNTRY"),
          value: "country.name",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "is_active",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("STOCK.WAREHOUSES.SYSTEM"),
          value: "is_system",
          align: "center",
          width: "9%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
  },
  watch: {
    stockContextVersion() {
      this.reloadTable();
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    this.userStatePrepared = true;
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      return error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-warehouses-table"]?.fetchServerData();
      });
    },
    handleCreateClick() {
      this.$router.push({ name: "stock-warehouses-create" });
    },
    handleEditClick(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );

      this.$router.push({ name: "stock-warehouses-edit", params: { id } });
    },
    handleDeleteClick(warehouse) {
      this.selectedWarehouse = warehouse;
      this.showDeleteConfirm = true;
    },
    deleteWarehouse() {
      if (!this.selectedWarehouse) {
        return;
      }

      this.isProcessing = true;
      this.$store
        .dispatch(DELETE_STOCK_WAREHOUSE, this.selectedWarehouse.id)
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          });
          this.reloadTable();
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
          this.selectedWarehouse = null;
        });
    },
  },
};
</script>
